import React from "react";
import PropTypes from "prop-types";
import Header from "./components/Header/Header";
import HeroSection from "./components/HeroSection/HeroSection";
import AboutSection from "./components/AboutSection/AboutSection";
import ContactSection from "./components/ContactSection/ContactSection";
import Footer from "./components/Footer/Footer";
import EditableSection, { editorShape } from "./components/EditableSection/EditableSection";
import { AboutBlockEditorContext } from "../../components/about-content/AboutContent";
import { sheetClass } from "./styles";
import { siteThemeStyle } from "./theme";

/**
 * Site1 — the public booking page, `/:organizationSlug`.
 *
 * Every visible string, image and colour is supplied by the caller from
 * BookingPageSettings (`buildSiteData` in mapSettings.js is that mapping); the
 * template itself holds no business content.
 *
 * The same component is what Site & widget and the setup wizard preview, with
 * an `editor`: each section then outlines itself, takes a click as "edit
 * this", and a section the business has not filled in yet shows where it
 * would go instead of disappearing — on the live page it simply is not there.
 */

/** Where an empty section would be, in the preview only. */
const Placeholder = ({ icon, text }) => (
  <div className="bg-white py-10">
    <div className={sheetClass}>
      <div className="flex min-h-28 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-ink-200 px-6 py-8 text-center text-ink-500">
        <i className={`bi ${icon} text-2xl text-ink-400`} aria-hidden="true" />
        <p className="m-0 max-w-md text-sm">{text}</p>
      </div>
    </div>
  </div>
);

Placeholder.propTypes = {
  icon: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

const Site1Template = ({ data, onNavigate = () => {}, editor = null }) => {
  const { logo, businessName, menuItems, colors, hero, about, contact, social, showsBadge } =
    data;

  return (
    // overflow-x-clip, not -hidden: it keeps a wide CMS element from scrolling
    // the page sideways without becoming a scroll container, which would stop
    // the header sticking.
    <div className="overflow-x-clip bg-white font-sans text-ink-900" style={siteThemeStyle(colors)}>
      <Header
        logo={logo}
        businessName={businessName}
        menuItems={menuItems}
        ctaText={hero.buttonText}
        onCta={hero.onButtonClick}
        onNavigate={onNavigate}
      />

      <main>
        <EditableSection editor={editor} section="header">
          <HeroSection
            title={hero.title}
            description={hero.description}
            imageUrl={hero.imageUrl}
            logo={logo}
            businessName={businessName}
            buttonText={hero.buttonText}
            onButtonClick={hero.onButtonClick}
          />
        </EditableSection>

        {about ? (
          // While About is being edited, its blocks are what is clicked on.
          <EditableSection editor={editor} section="about" open={editor?.active === "about"}>
            <AboutBlockEditorContext.Provider
              value={
                editor?.active === "about"
                  ? {
                      activeBlock: editor.activeBlock ?? null,
                      onSelectBlock: (index) => editor.onSelect("about", index),
                    }
                  : null
              }
            >
              <AboutSection
                title={about.title}
                label={about.label}
                text={about.text}
                onBook={hero.onButtonClick}
                bookingLabel={hero.buttonText}
              />
            </AboutBlockEditorContext.Provider>
          </EditableSection>
        ) : (
          editor?.placeholders?.about && (
            <EditableSection editor={editor} section="about">
              <Placeholder icon="bi-file-earmark-text" text={editor.placeholders.about} />
            </EditableSection>
          )
        )}

        {contact ? (
          <EditableSection editor={editor} section="contact">
            <ContactSection {...contact} />
          </EditableSection>
        ) : (
          editor?.placeholders?.contact && (
            <EditableSection editor={editor} section="contact">
              <Placeholder icon="bi-geo-alt" text={editor.placeholders.contact} />
            </EditableSection>
          )
        )}
      </main>

      <EditableSection editor={editor} section="social">
        <Footer businessName={businessName} logo={logo} social={social} showsBadge={showsBadge} />
      </EditableSection>
    </div>
  );
};

Site1Template.propTypes = {
  data: PropTypes.shape({
    logo: PropTypes.string,
    businessName: PropTypes.string,
    menuItems: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        href: PropTypes.string.isRequired,
      })
    ),
    colors: PropTypes.shape({
      primary: PropTypes.string,
      secondary: PropTypes.string,
    }),
    hero: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      imageUrl: PropTypes.string,
      buttonText: PropTypes.string,
      onButtonClick: PropTypes.func,
    }).isRequired,
    about: PropTypes.shape({
      title: PropTypes.string,
      label: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
    contact: PropTypes.shape({
      address: PropTypes.string,
      phone: PropTypes.string,
      email: PropTypes.string,
      openingHours: PropTypes.string,
    }),
    social: PropTypes.shape({
      facebookUrl: PropTypes.string,
      instagramUrl: PropTypes.string,
    }),
    // Whether this organization's plan carries the EasyPlanner line. Comes from
    // the page's own response, not a second call — a page that paints itself
    // unbranded and adds the line a moment later is worse than one that never
    // had it.
    showsBadge: PropTypes.bool,
  }).isRequired,
  onNavigate: PropTypes.func,
  /** Preview only: see EditableSection. */
  editor: editorShape,
};

export default Site1Template;
